import { useEffect, useState } from "react";
import {
  FaRobot,
  FaExclamationTriangle,
  FaArrowLeft,
  FaInfoCircle,
  FaChevronDown,
} from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import useAISummaryData from "../hooks/useAISummaryData";
import AISummarySkeleton from "./AISummarySkeleton";
import "github-markdown-css/github-markdown-light.css";
import "./AISummaryContent.css";
import "./AISummaryExpanded.css";

const HINT_AUTO_COLLAPSE_MS = 8000;

const AISummaryExpanded = ({ guuid, folder, onBack }) => {
  const {
    tabs,
    activeTab,
    setActiveTab,
    content,
    loading,
    listLoading,
    listError,
    fileList,
    hasPath,
  } = useAISummaryData(guuid, folder);
  const [hintExpanded, setHintExpanded] = useState(true);

  useEffect(() => {
    const id = setTimeout(() => setHintExpanded(false), HINT_AUTO_COLLAPSE_MS);
    return () => clearTimeout(id);
  }, []);

  return (
    <div className="ai-summary-expanded">
      <div className="ai-summary-expanded-header">
        <button
          type="button"
          className="ai-summary-back-button"
          onClick={onBack}
        >
          <FaArrowLeft aria-hidden="true" />
          Back to model details
        </button>
        <h2 className="ai-summary-expanded-title">AI Summary</h2>
      </div>

      <div className="ai-summary-hint">
        <button
          type="button"
          className={`ai-summary-hint-trigger${hintExpanded ? " expanded" : ""}`}
          onClick={() => setHintExpanded((prev) => !prev)}
          aria-expanded={hintExpanded}
          aria-controls="ai-summary-hint-content"
          id="ai-summary-hint-label"
        >
          <FaInfoCircle className="ai-summary-hint-icon" aria-hidden="true" />
          <span>About this summary</span>
          <FaChevronDown
            className={`ai-summary-hint-chevron${hintExpanded ? " expanded" : ""}`}
            aria-hidden="true"
          />
        </button>
        <div
          id="ai-summary-hint-content"
          role="region"
          aria-labelledby="ai-summary-hint-label"
          className={`ai-summary-hint-content${hintExpanded ? " expanded" : ""}`}
        >
          <div className="ai-summary-description">
            <div className="ai-summary-header">
              <FaRobot
                aria-hidden="true"
                style={{ fontSize: "1.4rem", color: "var(--color-accent)" }}
              />
              <span className="ai-summary-title">AI-Generated Insights</span>
            </div>
            <div className="ai-summary-main">
              This summary is <b>automatically generated</b> using advanced AI
              tools to help you quickly understand the key points of this
              modeling report.
            </div>
            <div className="ai-summary-note">
              <FaExclamationTriangle aria-hidden="true" />
              <span>
                <b>Note:</b> All content is extracted directly from the report —
                no external data or interpretations have been added.
              </span>
            </div>
            <div className="ai-summary-footer">
              For full context or detailed analysis, please refer to the
              complete report.
            </div>
          </div>
        </div>
      </div>

      {!hasPath ? (
        <div className="ai-summary-empty">
          <p>No summary data available for this model.</p>
        </div>
      ) : (
        <>
          {tabs.length > 0 && (
            <div className="summary-tabs">
              {tabs.map((tab, idx) => (
                <button
                  key={tab.filename}
                  type="button"
                  className={`summary-tab${idx === activeTab ? " active" : ""}`}
                  onClick={() => setActiveTab(idx)}
                >
                  {tab.name}
                </button>
              ))}
            </div>
          )}
          <div className="ai-summary-markdown markdown-body">
            {listLoading && !fileList?.length ? (
              <AISummarySkeleton />
            ) : listError ? (
              <div className="ai-summary-error">
                Could not load summary list: {listError}
              </div>
            ) : !tabs.length ? (
              <div className="ai-summary-empty">
                <p>No AI summaries available for this model.</p>
              </div>
            ) : loading ? (
              <AISummarySkeleton />
            ) : (
              <ReactMarkdown
                remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
              >
                {content}
              </ReactMarkdown>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AISummaryExpanded;
